import { Alert, AlertColor, Box } from '@mui/material';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import axiosInstance from '../../../utils/axios';
import { URLS } from '../../../utils/constants/urls';
import {
  BookingDetailActionsProvider,
  BookingDetailBottomPreview,
  BookingDetailTopToolbar,
} from './BookingDetailPageActions';
import { BookingDetailStickyHeader } from './BookingDetailStickyHeader';
import BookingDetailTabs from './BookingDetailTabs';
import { useLineItemBillingPayment } from './BookingDetailTabs/BookingBillingPaymentPanels';
import RelatedBooking from './RelatedBooking';
import { useDispatch } from 'react-redux';
import { setHourlyPricing } from 'utils/redux/reducer/booking-slice';
import { ScrollTop } from 'utils/validators/HelperFunctions';

const BookingDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams() as any;
  const [bookingLineItem, setBookingLinetItem] = useState<any>({ booking: {} });
  const [isWithinActualizationWindow, setIsWithinActualizationWindow] = useState<boolean>(false);
  const [isPastActualizationWindow, setIsPastActualizationWindow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [futureDateForCancel, setFutureDateForCancel] = useState<boolean>(false);
  const [futureDateForBeo, setFutureDateForBeo] = useState<boolean>(false);
  const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);
  const billingPayment = useLineItemBillingPayment(bookingLineItem, paymentRefreshKey);
  const [showExternalAlert, setShowExternalAlert] = useState<boolean>(true);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const getInitialResponse = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axiosInstance.get(URLS.getBookingLineItemById(id));
      checkFutureDateForBeo(data && data.startDate, data?.allowPastBeos);
      checkFutureTimeForCancelBooking(data?.startDatetime, data?.startDate);
      checkActualizationWindow(data && data.startDatetime, data.actualizationWindow);
      setBookingLinetItem(data);
      dispatch(setHourlyPricing(data.hourlyPricing));
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      if (id) {
        await getInitialResponse();
      }
    })();
  }, [getInitialResponse, id]);

  useEffect(() => {
    // Reset the dismissible external alert when navigating to a different booking
    setShowExternalAlert(true);
  }, [id]);

  const refreshAfterMutation = useCallback(() => {
    setPaymentRefreshKey(k => k + 1);
    getInitialResponse();
  }, [getInitialResponse]);

  const checkFutureDateForBeo = (date: string, allowPastBeos?: boolean) => {
    if (allowPastBeos) {
      setFutureDateForBeo(true);
      return;
    }
    if (date) {
      const yesterday = dayjs(new Date()).subtract(1, 'day');
      const bookingDate = dayjs(date);
      if (yesterday.isBefore(bookingDate)) {
        setFutureDateForBeo(true);
      } else if (yesterday.isSame(bookingDate)) {
        setFutureDateForBeo(true);
      } else {
        setFutureDateForBeo(false);
      }
    }
  };

  const checkFutureTimeForCancelBooking = (startDatetime?: string | number, startDate?: string) => {
    if (startDatetime != null && String(startDatetime).trim() !== '') {
      const currentDatetime = dayjs();
      const eventStart = dayjs.unix(parseInt(String(startDatetime), 10) / 1000);
      if (eventStart.isValid()) {
        setFutureDateForCancel(currentDatetime.isBefore(eventStart));
        return;
      }
    }
    if (startDate) {
      const yesterday = dayjs().subtract(1, 'day');
      const bookingDate = dayjs(startDate);
      setFutureDateForCancel(yesterday.isBefore(bookingDate) || yesterday.isSame(bookingDate, 'day'));
      return;
    }
    setFutureDateForCancel(false);
  };

  const checkActualizationWindow = (startDatetime: string, actualizationWindow: number) => {
    const currentDate = dayjs();
    const endDate = dayjs(startDatetime);
    const actualizationEnd = endDate.add(actualizationWindow, 'days');

    const isWithinActualizationWindow = currentDate.isBetween(endDate, actualizationEnd);
    const isPastActualizationWindow = endDate.isBefore(currentDate);
    setIsWithinActualizationWindow(isWithinActualizationWindow);
    setIsPastActualizationWindow(isPastActualizationWindow);
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  useEffect(() => {
    ScrollTop(0);
  }, [id]);
  return (
    <BookingDetailActionsProvider
      bookingLineItem={bookingLineItem}
      futureDateForCancel={futureDateForCancel}
      futureDateForBeo={futureDateForBeo}
      onSuccess={refreshAfterMutation}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={3000}
        />
      )}
      {isLoading && <PageLoader />}
      {/* {bookingLineItem?.status === 'ENQUIRY' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This is an <strong>Enquiry</strong>. To convert it into a booking, please <strong>clone</strong> the enquiry,
          update the dates, and set the status to <strong>Confirmed, Tentative, or Waitlisted</strong>.
        </Alert>
      )} */}
      {(bookingLineItem?.booking?.externalBooking || bookingLineItem?.externalBooking) && showExternalAlert && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setShowExternalAlert(false)}>
          This booking was imported from IDS. Prices shown here may not be accurate. Editing this booking will also
          update it in IDS.
        </Alert>
      )}
      <Box sx={{ mx: { xs: 0, md: -3 }, mt: -1, width: '100%', maxWidth: '100%' }}>
        <BookingDetailTopToolbar />
        {!isLoading && bookingLineItem?.id ? (
          <BookingDetailStickyHeader
            bookingLineItem={bookingLineItem}
            paymentSummary={billingPayment.paymentSummary}
          />
        ) : null}
      </Box>
      <BookingDetailTabs
        isPastActualizationWindow={isPastActualizationWindow}
        isWithinActualizationWindow={isWithinActualizationWindow}
        bookingLineItem={bookingLineItem}
        onSuccess={refreshAfterMutation}
        relatedBookingLineItems={bookingLineItem?.relatedBookings}
        billingPayment={billingPayment}
      />
      <RelatedBooking
        relatedBookingLineItems={bookingLineItem?.relatedBookings}
        currentLineItemId={bookingLineItem?.id}
      />
      <BookingDetailBottomPreview />
    </BookingDetailActionsProvider>
  );
};
export default BookingDetail;
